import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AppFontAwesomeIcon, AppRemixIcon } from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import colors from '../../../../../../constants/colors';
import StateHistory from './IconBlock/StateHistory';
import FavouriteObligation from './IconBlock/FavouriteObligation';
import MaintenanceCheckIcon from './IconBlock/MaintenanceCheckIcon';
import Translator from '../../../../../components/helpers/Translator';
import PlantObligationTagsIconList from './IconBlock/PlantObligationTagsIconList';
import SaicaGoalStateWarning from './IconBlock/SaicaGoalStateWarning';
import SaicaWeight from './IconBlock/SaicaWeight';
import ObligationAssociation from './IconBlock/ObligationAssociation';

const IconListBlock = (props) => {
  const { plantObligationID } = props;
  const { moduleBridgeHex } = useParams();
  const poLevel2 = useSelector(
    (state) => state.currentPage?.plantObligationLevels?.level2
  );
  const poLevel3 = useSelector(
    (state) => state.currentPage?.plantObligationLevels?.level3
  );
  const currentPage = useSelector((state) => state.currentPage);
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex].permissions
  );
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const plantPermissions = useSelector(
    (state) => state.auth.permissions.client.gcl.module.plant
  );

  return (
    <>
      <FavouriteObligation
        isFavourite={poLevel3[plantObligationID]?.alerts?.isFavourite}
        plantObligationID={plantObligationID}
      />
      <AppRemixIcon
        icon="notification-2"
        title={ReactDOMServer.renderToString(
          poLevel3[plantObligationID]?.alerts?.modificationWarning ? (
            <Translator
              file="module-legalCompliance"
              name="obligation.modified"
              variables={{
                date: poLevel3[plantObligationID]?.alerts?.modificationWarning,
              }}
            />
          ) : (
            <Translator
              file="module-legalCompliance"
              name="obligation.without.modifications"
            />
          )
        )}
        color={
          poLevel3[plantObligationID]?.alerts?.modificationWarning
            ? colors.modifiedObligations
            : colors.grey300
        }
        filled={poLevel3[plantObligationID]?.alerts?.modificationWarning}
      />
      {permissions?.gcl?.module?.plant?.gclResponsibles && (
        <>
          <AppRemixIcon
            icon="user"
            title={
              currentPage?.responsibleTabs?.[plantObligationID]
                ? (
                  currentPage?.responsibleTabs?.[plantObligationID]?.length >
                  0 &&
                  currentPage?.responsibleTabs?.[plantObligationID]?.filter(
                    (r) => r.value === userBridge.id
                  )
                ).length > 0
                  ? ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="obligation.you.are.responsible"
                    />
                  )
                  : ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="obligation.you.are.not.responsible"
                    />
                  )
                : poLevel3[plantObligationID]?.alerts?.userAssigned
                  ? ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="obligation.you.are.responsible"
                    />
                  )
                  : ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="obligation.you.are.not.responsible"
                    />
                  )
            }
            color={
              currentPage?.responsibleTabs?.[plantObligationID]
                ? (
                  currentPage?.responsibleTabs?.[plantObligationID]?.length >
                  0 &&
                  currentPage?.responsibleTabs?.[plantObligationID]?.filter(
                    (r) => r.value === userBridge.id
                  )
                ).length > 0
                  ? colors.primaryLight
                  : colors.grey300
                : poLevel3[plantObligationID]?.alerts?.userAssigned
                  ? colors.primaryLight
                  : colors.grey300
            }
          />
          <AppRemixIcon
            icon="group"
            title={
              currentPage?.responsibleTabs?.[plantObligationID]
                ? currentPage?.responsibleTabs?.[plantObligationID]?.length > 0
                  ? ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="obligation.has.responsibles"
                    />
                  )
                  : ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="obligation.has.no.responsibles"
                    />
                  )
                : poLevel3[plantObligationID]?.alerts?.usersAssigned
                  ? ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="obligation.has.responsibles"
                    />
                  )
                  : ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="obligation.has.no.responsibles"
                    />
                  )
            }
            color={
              currentPage?.responsibleTabs?.[plantObligationID]
                ? currentPage?.responsibleTabs?.[plantObligationID]?.length > 0
                  ? colors.primaryLight
                  : colors.grey300
                : poLevel3[plantObligationID]?.alerts?.usersAssigned
                  ? colors.primaryLight
                  : colors.grey300
            }
          />
        </>
      )}
      <StateHistory
        state={poLevel3[plantObligationID]?.alerts?.stateHistory}
        plantObligationID={plantObligationID}
      />
      {permissions?.gcl?.module?.submodules?.correctiveActions &&
        currentPage.options.hasCaSubmodule && (
          <AppRemixIcon
            icon="feedback"
            title={
              poLevel3[plantObligationID]?.alerts?.pendingTasks
                ? ReactDOMServer.renderToString(
                  <Translator
                    file="module-legalCompliance"
                    name="action.plan.pending"
                  />
                )
                : ReactDOMServer.renderToString(
                  <Translator
                    file="module-legalCompliance"
                    name="no.action.plan"
                  />
                )
            }
            color={
              poLevel3[plantObligationID]?.alerts?.pendingTasks
                ? colors.primaryLight
                : colors.grey300
            }
            filled={poLevel3[plantObligationID]?.alerts?.pendingTasks}
          />
        )}
      {permissions?.gcl?.module?.api?.send &&
        !permissions?.gcl?.module?.api?.send?.noBlock && (
          <AppRemixIcon
            icon="lock"
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name={
                  poLevel3[plantObligationID]?.alerts?.apiLock
                    ? 'block.by.no.acceptance'
                    : 'no.block.by.no.acceptance'
                }
              />
            )}
            color={
              poLevel3[plantObligationID]?.alerts?.apiLock
                ? colors.danger
                : colors.grey300
            }
            filled={poLevel3[plantObligationID]?.alerts?.apiLock}
          />
        )}
      {permissions?.gcl?.module?.plant?.maintenanceCheck && (
        <MaintenanceCheckIcon
          isMaintenanceCheck={
            poLevel3[plantObligationID]?.alerts?.maintenanceCheck
          }
          plantObligationID={plantObligationID}
        />
      )}
      {poLevel2?.[plantObligationID]?.obligation?.applicableDate && (
        <AppRemixIcon
          icon="history"
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-legalCompliance"
              name="applicable.from"
              variables={{
                applicableDate:
                  poLevel2?.[plantObligationID]?.obligation?.applicableDate,
              }}
            />
          )}
          color={colors.state2}
          filled
        />
      )}
      {permissions?.gcl?.module?.plant?.calendar?.customEmailNotifications &&
        poLevel3[plantObligationID]?.alerts?.calendarSettings &&
        userBridge?.permissions?.email && (
          <AppRemixIcon
            icon="mail-check"
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="emails.custom.settings"
              />
            )}
            color={colors.primaryLight}
            filled
          />
        )}
      {poLevel3[plantObligationID]?.obligationGroupType1 && (
        <AppRemixIcon
          icon={poLevel3[plantObligationID]?.obligationGroupType1.icon}
          title={poLevel3[plantObligationID]?.obligationGroupType1.name}
          color={poLevel3[plantObligationID]?.obligationGroupType1.color}
          className="text-center"
          filled
        />
      )}
      {poLevel3[plantObligationID]?.obligationGroupType2 && (
        <AppFontAwesomeIcon
          icon={poLevel3[plantObligationID]?.obligationGroupType2.icon}
          title={poLevel3[plantObligationID]?.obligationGroupType2.name}
          color={poLevel3[plantObligationID]?.obligationGroupType2.color}
          className="text-center"
        />
      )}
      {permissions?.global?.corporateObligations &&
        poLevel3[plantObligationID]?.alerts?.corporateObligation && (
          <AppRemixIcon
            icon="building-4"
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="corporate.obligation"
              />
            )}
            color={colors.danger}
            filled
          />
        )}
      {permissions?.gcl?.module?.plant?.stageGates &&
        currentPage?.options?.stageGates?.criticalPlantObligationIds &&
        currentPage?.options?.stageGates?.criticalPlantObligationIds.includes(
          plantObligationID
        ) && (
          <AppRemixIcon
            icon="alert"
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="critical.question"
              />
            )}
            color={colors.danger}
            filled
          />
        )}
      {(poLevel3[plantObligationID]?.alerts?.masterHierarchicalObligation ||
        poLevel3[plantObligationID]?.alerts
          ?.childrenHierarchicalObligation) && (
          <AppRemixIcon
            icon="links"
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name={
                  poLevel3[plantObligationID]?.alerts
                    ?.masterHierarchicalObligation
                    ? 'master.linked.obligation'
                    : 'children.linked.obligation'
                }
              />
            )}
            color={
              poLevel3[plantObligationID]?.alerts?.masterHierarchicalObligation
                ? colors.danger
                : colors.primary
            }
          />
        )}
      {
        permissions?.gcl?.module?.plant?.showIconObligationAssociations &&
        permissions?.gcl?.module?.plant?.showIconObligationAssociations === true &&
        poLevel3[plantObligationID]?.showIconObligationAssociations &&
        poLevel3[plantObligationID]?.showIconObligationAssociations === true && (
          <ObligationAssociation
            description={poLevel3[plantObligationID].descriptionObligationAssociation ?? ''}
            plantObligationID={plantObligationID}
          />
        )
      }
      <PlantObligationTagsIconList plantObligationID={plantObligationID} />
      <SaicaGoalStateWarning plantObligationID={plantObligationID} />
      <SaicaWeight plantObligationID={plantObligationID} />
    </>
  );
};

export default IconListBlock;
